import { useSelector } from 'react-redux'
import { createSelector, Selector } from '@reduxjs/toolkit'
import {
  Tenant,
  Hub,
  HubType,
  ShareTargetType,
  CustomFieldUsage,
} from '@alucio/aws-beacon-amplify/src/models'
import { RootState } from '../store'
import { ORMTypes, HubORM, SharePermissionORM } from 'src/types/types';
import { getMappedCustomValues } from './common';
import { getMergedHub } from '../hub/hubUtils';
import { getIndexedValidCustomFields, matchesLockedFilters } from 'src/state/datastore/query';
import { CurrentUser, loggedUser } from 'src/state/redux/selector/user'

export type HubORMMap = { [hubId: string]: HubORM }
export const selTenants = (state: RootState): Tenant[] => state.tenant.records
export const selHubs = (state: RootState): Hub[] => state.hub.records;
export const selHubId = (_, __, hubId: string) => hubId;

const toHubORM = (
  hub: Hub,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  tenants: Tenant[],
  hubs: Hub[],
  loggedUser: CurrentUser,
  // contentShares: ContentShare[],
): HubORM => {
  // [TODO-2780] - Technically there should only be one tenant
  //               And it should always match, You can probably use a ! here
  //             - This would be safe
  const [tenant] = tenants

  const sharedPermissions = (hub.sharedPermissions || []).reduce<SharePermissionORM[]>((acc, permission) => {
    if (permission.isDeleted) {
      return acc;
    }
    const customFilterValues = getMappedCustomValues(
      { internalUsages: [CustomFieldUsage.USER_FILTER] },
      permission.targetCustomValues,
      tenant?.config.customFields);

    const validIndexedCustomFields = getIndexedValidCustomFields(tenant?.config?.customFields || []);
    const shouldCheckFilters = !permission.isDeleted && permission.targetCustomValues?.length
    const templateFiltersMatchesUserFilters = shouldCheckFilters ? matchesLockedFilters(
      loggedUser?.userProfile?.lockedFiltersCustomValues || [],
      validIndexedCustomFields,
    )(false, permission.targetCustomValues) : false

    const isValidUserPermission =
      permission.targetUsername === loggedUser?.userProfile?.id ||
      permission.targetType === ShareTargetType.ALL

    acc.push({
      model: permission,
      type: ORMTypes.SHARE_PERMISSION,
      meta: {
        isValidPermission: templateFiltersMatchesUserFilters || isValidUserPermission,
        customFilterValues,
      },
    });

    return acc;
  }, []);

  const hubTemplate = hubs.find(currentHub => currentHub.id === hub.templateId)

  const hubORM: HubORM = {
    model: getMergedHub(hub, hubTemplate),
    type: ORMTypes.HUB,
    meta: {
    },
    relations: {
      sharedPermissions,
    },
  }

  return hubORM
}

const allHubsList: Selector<RootState, HubORM[]> = createSelector(
  selTenants,
  selHubs,
  loggedUser,
  (tenants, hubs, loggedUser): HubORM[] => hubs.map(hub => toHubORM(hub, tenants, hubs, loggedUser)),
);

const hubsStandaloneAndTemplateInstance: Selector<RootState, HubORM[]> = createSelector(
  allHubsList,
  (hubs): HubORM[] => hubs
    .filter(hub => hub.model.hubType === HubType.STANDALONE || hub.model.hubType === HubType.TEMPLATE_INSTANCE),
)

const hubTemplatesList: Selector<RootState, HubORM[]> = createSelector(
  allHubsList,
  (hubs): HubORM[] => hubs.filter(hub => {
    const isTemplate = hub.model.hubType === HubType.TEMPLATE
    return (
      (isTemplate) ||
      (hub.relations.sharedPermissions.some(permission => permission.meta.isValidPermission))
    )
  }),
)

export const hub: Selector<RootState, HubORM | undefined> = createSelector(
  allHubsList,
  selHubId,
  (hubs, hubId): HubORM | undefined =>
    hubs.find(({ model }) => model.id === hubId),
)

export const useHubList = (): ReturnType<typeof allHubsList> =>
  useSelector((state: RootState) => allHubsList(state));

export const useHubsStandaloneAndTemplateInstance = (): ReturnType<typeof hubsStandaloneAndTemplateInstance> =>
  useSelector((state: RootState) => hubsStandaloneAndTemplateInstance(state));

export const useHub = (hubId: string): ReturnType<typeof hub> =>
  useSelector((state: RootState) => hub(state, undefined, hubId));

export const useHubTemplatesList = (): ReturnType<typeof hubTemplatesList> =>
  useSelector((state: RootState) => hubTemplatesList(state));
